import {
  GiftOrder,
  GiftSubmission,
  SubmitGiftKycResponse,
  SubmitGiftKycPayload,
  GiftOutboundOrder,
  DigioAccessToken,
  DigioKycRequest,
  DigioKycRequestStatus,
  GiftFormDetails,
} from "../types/gift";
import { api } from "./axios";

export const submitGiftKycApi = (
  payload: SubmitGiftKycPayload
): Promise<SubmitGiftKycResponse> => {
  const formData = new FormData();

  formData.append("investment_account_guid", payload.investment_account_guid);
  formData.append("amc", payload.amc);
  formData.append("ckyc_number", payload.ckyc_number);
  formData.append("father_name", payload.father_name);
  if (payload.occupation) formData.append("occupation", payload.occupation);
  if (payload.bank_swift_code)
    formData.append("bank_swift_code", payload.bank_swift_code);

  if (payload.pan_card_image)
    formData.append("pan_card_image", payload.pan_card_image);
  if (payload.address_proof_image)
    formData.append("address_proof_image", payload.address_proof_image);
  if (payload.cancelled_cheque_image)
    formData.append("cancelled_cheque_image", payload.cancelled_cheque_image);
  if (payload.pan_card_password)
    formData.append("pan_card_password", payload.pan_card_password);
  if (payload.address_proof_password)
    formData.append("address_proof_password", payload.address_proof_password);
  if (payload.cancelled_cheque_password)
    formData.append(
      "cancelled_cheque_password",
      payload.cancelled_cheque_password
    );
  if (payload.name_of_company)
    formData.append("name_of_company", payload.name_of_company);
  if (payload.company_address)
    formData.append("company_address", payload.company_address);
  if (payload.nature_of_business)
    formData.append("nature_of_business", payload.nature_of_business);

  return api.post("gift-city-form/", formData);
};

export function generateSigningRequest(payload: {
  formId: number;
  sendEmail?: boolean;
}): Promise<{
  form_id: number;
  document_id: string;
  self_signed: boolean;
  self_signed_type: string;
  access_token?: DigioAccessToken;
}> {
  return api.post("gift-city-form/send-signing-link/", {
    form_id: payload.formId,
    send_email_to_client: payload.sendEmail,
  });
}

export function getGiftLatestSumissionInfo(payload: {
  investmentAccountId: string;
}): Promise<GiftFormDetails> {
  return api.get("gift-city-form/details/", {
    params: { investment_account_guid: payload.investmentAccountId },
  });
}

export function getGiftSubmissions(
  investmentAccountId: string
): Promise<GiftSubmission[]> {
  return api.get("gift-city-form/", {
    params: { investment_account_id: investmentAccountId },
  });
}

type CreateGiftOrderPayload = {
  bankAccountId: number;
  amount: number;
  folioNo: string;
  fundSchemeId: number;
  bankCustomerId?: string;
  investmentAccountId?: string;
  idempotencyKey?: string;
};

const PROVIDER = "glomopay";

export function createGiftOrder(payload: CreateGiftOrderPayload) {
  return api.post(
    `${PROVIDER}/orders/`,
    {
      investor_bank_account_id: payload.bankAccountId,
      destination_amount: payload.amount,
      folio_number: payload.folioNo,
      fund_scheme_id: payload.fundSchemeId,
      bank_customer_id: payload.bankCustomerId,
      investment_account_guid: payload.investmentAccountId,
    },
    {
      headers: {
        "Idempotency-Key": payload.idempotencyKey ?? crypto.randomUUID(),
      },
    }
  );
}

export function getGiftOrder(payload: { orderId: number }): Promise<GiftOrder> {
  return api.get(`${PROVIDER}/orders/${payload.orderId}/`);
}

export function getGiftOutboundOrder(payload: {
  orderId: number;
}): Promise<GiftOutboundOrder> {
  return api.get(`${PROVIDER}/gift-outbound/${payload.orderId}/`);
}

type LrxFxQuotePayload = {
  bankAccountId: number;
  amount: number;
  fundSchemeId: number;
};

export interface LrsFxQuote {
  id: string;
  from: {
    amount: string;
    currency: string;
  };
  to: {
    amount: string;
    currency: string;
  };
  fx_rate: string;
  bank_code: string;
  total_tax: string;
  expires_at: string;
  created_at: string;
  service_fee: {
    amount: string;
    currency: string;
  };
}

export function getLRSFXQuote(payload: LrxFxQuotePayload): Promise<LrsFxQuote> {
  return api.post(`${PROVIDER}/quotes/`, {
    investor_bank_account_id: payload.bankAccountId,
    destination_amount: payload.amount,
    fund_scheme_id: payload.fundSchemeId,
  });
}

export function getPaymentGatewayToken(payload: {
  fundSchemeId: number;
}): Promise<{
  public_key: string;
  environment: string;
}> {
  return api.get(`${PROVIDER}/config/`, {
    params: { fund_scheme_id: payload.fundSchemeId },
  });
}

export function notifyGiftPaymentLink(payload: { id: number }): Promise<void> {
  return api.post(`${PROVIDER}/gift-outbound/${payload.id}/link/`, {
    send_link: true,
  });
}

type CreateGiftOutboundOrderPayload = {
  bankAccountId: number;
  amount: number;
  folioNo: string;
  fundSchemeId: number;
  investmentAccountId?: string;
};

export function createGiftOutboundOrder(
  payload: CreateGiftOutboundOrderPayload
): Promise<GiftOutboundOrder> {
  return api.post(`${PROVIDER}/gift-outbound/`, {
    investor_bank_account_id: payload.bankAccountId,
    destination_amount: payload.amount,
    folio_number: payload.folioNo,
    fund_scheme_id: payload.fundSchemeId,
    investment_account_guid: payload.investmentAccountId,
  });
}

export function verifySdkResponse(payload: {
  paymentId: string;
  status: string;
  signature: string;
  orderId: string;
}) {
  return api.post(`${PROVIDER}/orders/${payload.orderId}/verify/`, {
    payment_id: payload.paymentId,
    status: payload.status,
    signature: payload.signature,
  });
}

export async function getExchangeRates(payload: {
  currency?: string;
  startDate: string;
  endDate: string;
}): Promise<{ currency: string; rate: string; date: string }[]> {
  return api.get("exchange-rates/", {
    params: {
      start_date: payload.startDate,
      end_date: payload.endDate,
      currency: payload.currency,
    },
  });
}

export function createDigioKycRequest(payload: {
  formId: number;
  clientId: number;
}): Promise<DigioKycRequest> {
  return api.post("digio/kyc/create-request/", {
    form_id: payload.formId,
    client_id: payload.clientId,
  });
}

export function getDigioKycRequestStatus(payload: {
  formId: number;
  clientId: number;
}): Promise<DigioKycRequestStatus> {
  return api.post("digio/kyc/request-status/", {
    form_id: payload.formId,
    client_id: payload.clientId,
  });
}
